import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT, COLORS, CARD_WIDTH, CARD_HEIGHT } from '../utils/Constants';
import { GameState, isHumanTurn, getCurrentPlayer } from '../game/GameState';
import { executeAction, GameAction, GameEvent } from '../game/GameActions';
import { canDrawFromDeck, canDrawFromDiscard, canMeld, canDiscard, canGoOut, canDiscardCard } from '../game/GameRules';
import { getAIAction } from '../game/AI';
import { Card, isWild, sortHand } from '../utils/CardUtils';
import { validateMeld, canAddToMeld } from '../game/Meld';
import { calculateRoundScore } from '../game/Scoring';
import { HandDisplay } from '../ui/HandDisplay';
import { MeldDisplay } from '../ui/MeldDisplay';
import { DeckDisplay } from '../ui/DeckDisplay';
import { DiscardPile } from '../ui/DiscardPile';
import { HUD } from '../ui/HUD';
import { CardSprite } from '../ui/CardSprite';

export class GameScene extends Phaser.Scene {
  private gameState!: GameState;
  private playerHand!: HandDisplay;
  private aiHand!: HandDisplay;
  private playerMelds!: MeldDisplay;
  private aiMelds!: MeldDisplay;
  private deckDisplay!: DeckDisplay;
  private discardPile!: DiscardPile;
  private hud!: HUD;

  // Selection state
  private selectedCards: string[] = [];
  private targetMeldId: string | null = null;
  private aiThinking = false;
  private aiThinkingText: Phaser.GameObjects.Text | null = null;
  private aiThinkingDots = 0;
  private aiThinkingTimer?: Phaser.Time.TimerEvent;

  // Draw animation / highlight
  private lastDrawnCardIds: string[] = [];
  private animating = false;

  // (pendingDraw removed — deck click draws immediately)

  // UI buttons
  private meldButton!: Phaser.GameObjects.Container;
  private discardButton!: Phaser.GameObjects.Container;
  private cancelButton!: Phaser.GameObjects.Container;

  constructor() {
    super({ key: 'GameScene' });
  }

  create(): void {
    // Table background with felt texture
    this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, COLORS.TABLE_GREEN);

    // Subtle radial gradient overlay
    const overlay = this.add.ellipse(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH * 1.2, GAME_HEIGHT * 1.2, COLORS.TABLE_FELT, 0.3);
    overlay.setDepth(0);

    // Initialize game
    const result = executeAction({} as GameState, { type: 'NEW_GAME' });
    this.gameState = result.state;

    // Create UI components
    this.createUI();
    this.refreshDisplay();
  }

  private createUI(): void {
    // Player hand (bottom)
    this.playerHand = new HandDisplay(this, GAME_WIDTH / 2, GAME_HEIGHT - 85, true);
    this.playerHand.setOnCardClick((card) => this.onPlayerCardClick(card));
    this.playerHand.setOnReorder((newOrder) => {
      // Update the hand in game state to match new order
      this.gameState.players[0].hand = [...newOrder];
    });
    this.playerHand.createSortButton();
    this.playerHand.setOnDragToDiscard((card) => this.onDragDiscard(card));
    this.playerHand.setOnDragToMeld((card, meldId) => this.onDragToMeld(card, meldId));

    // AI hand (top)
    this.aiHand = new HandDisplay(this, GAME_WIDTH / 2, 75, false);

    // Player melds (below center piles, above player hand)
    this.playerMelds = new MeldDisplay(this, 100, GAME_HEIGHT / 2 + 120);
    this.playerMelds.setOnMeldClick((meldId) => this.onMeldClick(meldId));

    // AI melds (above center piles, below AI hand)
    this.aiMelds = new MeldDisplay(this, 100, GAME_HEIGHT / 2 - 120);

    // Deck (centered pair: draw pile slightly left of center)
    const deckX = GAME_WIDTH / 2 - 45;
    const deckY = GAME_HEIGHT / 2;
    const pozzettoX = GAME_WIDTH / 4; // Midpoint between center and screen left
    this.deckDisplay = new DeckDisplay(this, deckX, deckY, pozzettoX, deckY);
    this.deckDisplay.create();
    this.deckDisplay.setOnDeckClick(() => this.onDeckClick());

    // Discard pile (slightly right of center)
    const discardX = GAME_WIDTH / 2 + 45;
    this.discardPile = new DiscardPile(this, discardX, deckY);
    this.discardPile.create();
    this.discardPile.setOnDiscardClick(() => this.onDiscardPileClick());

    // HUD
    this.hud = new HUD(this);
    this.hud.create();
    this.hud.setOnSettingsApply(() => this.refreshDisplay());

    // Set up drop zone checkers for drag-to-discard/meld
    this.playerHand.setDropZoneCheckers({
      isOverDiscard: (x: number, y: number) => {
        const dz = this.discardPile.getDropZone();
        return x >= dz.x - dz.width / 2 && x <= dz.x + dz.width / 2
          && y >= dz.y - dz.height / 2 && y <= dz.y + dz.height / 2;
      },
      getMeldAt: (x: number, y: number) => {
        return this.playerMelds.getMeldAtPosition(x, y);
      },
    });

    // Action buttons
    // Action buttons (positioned between center piles and player hand)
    const buttonY = GAME_HEIGHT / 2 + 80;
    this.meldButton = this.createActionButton(GAME_WIDTH / 2 - 140, buttonY, 'Play Meld', () => this.playSelectedMeld());
    this.meldButton.setVisible(false);

    this.discardButton = this.createActionButton(GAME_WIDTH / 2, buttonY, 'Discard', () => this.discardSelectedCard());
    this.discardButton.setVisible(false);

    this.cancelButton = this.createActionButton(GAME_WIDTH / 2 + 140, buttonY, 'Cancel', () => this.clearSelection());
    this.cancelButton.setVisible(false);

    // AI thinking indicator
    this.aiThinkingText = this.add.text(GAME_WIDTH / 2, 130, '', {
      fontSize: '16px',
      color: '#f39c12',
      fontFamily: 'Georgia, serif',
      fontStyle: 'italic',
    }).setOrigin(0.5).setDepth(200).setVisible(false);

    // Home button (top left)
    const homeBtn = this.add.text(15, 15, 'Home', {
      fontSize: '16px',
      color: '#ffcc00',
      fontFamily: 'Georgia, serif',
    }).setOrigin(0, 0).setDepth(200)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => this.showQuitConfirmation())
      .on('pointerover', () => homeBtn.setColor('#ffffff'))
      .on('pointerout', () => homeBtn.setColor('#ffcc00'));
  }

  private createActionButton(x: number, y: number, label: string, callback: () => void): Phaser.GameObjects.Container {
    const container = this.add.container(x, y).setDepth(250);

    const bg = this.add.rectangle(0, 0, 120, 36, COLORS.BUTTON, 0.9)
      .setInteractive({ useHandCursor: true })
      .on('pointerover', () => bg.setFillStyle(COLORS.BUTTON_HOVER, 1))
      .on('pointerout', () => bg.setFillStyle(COLORS.BUTTON, 0.9))
      .on('pointerdown', callback);

    const text = this.add.text(0, 0, label, {
      fontSize: '14px',
      color: COLORS.TEXT_LIGHT,
      fontFamily: 'Georgia, serif',
    }).setOrigin(0.5);

    container.add([bg, text]);
    return container;
  }

  private refreshDisplay(): void {
    // Update all UI from game state
    const highlightIds = this.lastDrawnCardIds.length > 0 ? [...this.lastDrawnCardIds] : undefined;
    this.lastDrawnCardIds = [];

    this.playerHand.updateCards(this.gameState.players[0].hand, isHumanTurn(this.gameState), highlightIds);
    this.aiHand.updateCards(this.gameState.players[1].hand, false);

    this.playerMelds.updateMelds(this.gameState.players[0].melds);
    this.aiMelds.updateMelds(this.gameState.players[1].melds);

    this.deckDisplay.updateDeckCount(this.gameState.drawPile.length);
    this.deckDisplay.updatePozzetti(this.gameState.pozzetti);

    this.discardPile.updateCards(this.gameState.discardPile);

    this.hud.update(this.gameState);

    // Update selection UI
    this.updateSelectionUI();

    // If it's AI's turn, trigger AI
    if (!isHumanTurn(this.gameState) && this.gameState.turnPhase !== 'round_end' && this.gameState.turnPhase !== 'game_over') {
      this.startAITurn();
    }
  }

  private updateSelectionUI(): void {
    if (this.selectedCards.length > 0) {
      this.playerHand.highlightCards(this.selectedCards);

      const player = this.gameState.players[0];
      const cards = this.selectedCards
        .map(id => player.hand.find(c => c.id === id))
        .filter((c): c is Card => c !== undefined);

      // Show meld button if 3+ valid cards
      const canPlayMeld = cards.length >= 3 && validateMeld(cards) !== null && this.gameState.hasDrawnThisTurn;
      this.meldButton.setVisible(canPlayMeld);

      // Show discard button if exactly 1 card selected and has drawn
      const canDiscardSelected = this.selectedCards.length === 1 && this.gameState.hasDrawnThisTurn;
      this.discardButton.setVisible(canDiscardSelected);

      this.cancelButton.setVisible(true);
    } else {
      this.playerHand.clearHighlights();
      this.meldButton.setVisible(false);
      this.discardButton.setVisible(false);
      this.cancelButton.setVisible(false);
    }
  }

  // --- Player Interactions ---

  private onPlayerCardClick(card: Card): void {
    if (!isHumanTurn(this.gameState) || this.animating) return;

    // If player is currently holding a card for reorder, ignore (placement handled by HandDisplay)
    if (this.playerHand.isHoldingCard()) return;

    // Check if there's a target meld
    if (this.gameState.hasDrawnThisTurn && this.targetMeldId) {
      this.addCardToTargetMeld(card);
      return;
    }

    if (!this.gameState.hasDrawnThisTurn) {
      // Before drawing: click picks up card for reordering
      const cardIndex = this.gameState.players[0].hand.findIndex(c => c.id === card.id);
      if (cardIndex >= 0) {
        this.playerHand.pickUpCard(cardIndex);
      }
      return;
    }

    // After drawing: toggle selection for meld/discard
    // If clicking an already-selected card that's the only selection, pick it up for reorder
    if (this.selectedCards.length === 1 && this.selectedCards[0] === card.id) {
      const cardIndex = this.gameState.players[0].hand.findIndex(c => c.id === card.id);
      if (cardIndex >= 0) {
        this.selectedCards = [];
        this.updateSelectionUI();
        this.playerHand.pickUpCard(cardIndex);
      }
      return;
    }

    // Toggle selection
    const idx = this.selectedCards.indexOf(card.id);
    if (idx >= 0) {
      this.selectedCards.splice(idx, 1);
    } else {
      this.selectedCards.push(card.id);
    }
    this.updateSelectionUI();
  }

  private async onDeckClick(): Promise<void> {
    if (!isHumanTurn(this.gameState) || this.animating) return;

    if (!canDrawFromDeck(this.gameState)) {
      this.hud.showMessage('Cannot draw from deck now', 1500);
      return;
    }

    const handSizeBefore = this.gameState.players[0].hand.length;
    const result = executeAction(this.gameState, { type: 'DRAW_DECK' });
    if (result.success) {
      this.gameState = result.state;
      const newCards = this.gameState.players[0].hand.slice(handSizeBefore);
      this.lastDrawnCardIds = newCards.map(c => c.id);

      // Animate card from deck to hand with flip
      if (newCards.length > 0) {
        this.animating = true;
        const deckPos = this.deckDisplay.getDeckPosition();
        const targetPos = this.playerHand.getCardPosition(
          this.gameState.players[0].hand.length - 1,
          this.gameState.players[0].hand.length,
        );
        const tempSprite = new CardSprite(this, deckPos.x, deckPos.y, newCards[0], false);
        tempSprite.setDepth(300);
        await tempSprite.animateToWithFlip(targetPos.x, targetPos.y, newCards[0], 400);
        tempSprite.destroy();
        this.animating = false;
      }

      this.clearSelection();
      this.refreshDisplay();
    }
  }

  private async onDiscardPileClick(): Promise<void> {
    if (!isHumanTurn(this.gameState) || this.animating) return;

    // If already drawn, clicking discard just toggles spread view
    if (this.gameState.hasDrawnThisTurn) {
      if (this.discardPile.isExpanded()) {
        // Collapse is handled internally by DiscardPile toggle
      }
      this.discardPile.setOnDiscardClick(() => this.onDiscardPileClick());
      // Toggle the expand/collapse via the spread button, or just expand here
      return;
    }

    if (!canDrawFromDiscard(this.gameState)) {
      this.hud.showMessage('Cannot draw from discard pile', 1500);
      return;
    }

    const discardPos = this.discardPile.getPosition();
    const pickedUpCards = [...this.gameState.discardPile];

    const result = executeAction(this.gameState, { type: 'DRAW_DISCARD' });
    if (result.success) {
      this.gameState = result.state;

      // Track all picked-up cards for highlighting
      this.lastDrawnCardIds = pickedUpCards.map(c => c.id);

      // Animate top card from discard to hand
      await this.animateDrawFromDiscard(discardPos);

      this.clearSelection();
      this.hud.showMessage(`Picked up ${pickedUpCards.length} card${pickedUpCards.length !== 1 ? 's' : ''}`, 1000);
      this.refreshDisplay();
    }
  }

  private async animateDrawFromDeck(newCards: Card[]): Promise<void> {
    if (newCards.length === 0) return;
    this.animating = true;

    const deckPos = this.deckDisplay.getDeckPosition();
    const targetPos = this.playerHand.getCardPosition(
      this.gameState.players[0].hand.length - 1,
      this.gameState.players[0].hand.length,
    );

    // Create a temporary face-down card at deck position
    const tempSprite = new CardSprite(this, deckPos.x, deckPos.y, newCards[0], false);
    tempSprite.setDepth(300);

    // Animate to hand position
    await tempSprite.animateTo(targetPos.x, targetPos.y, 400, 0);

    tempSprite.destroy();
    this.animating = false;
  }

  private async animateDrawFromDiscard(fromPos: { x: number; y: number }): Promise<void> {
    this.animating = true;

    const targetPos = this.playerHand.getCardPosition(
      this.gameState.players[0].hand.length - 1,
      this.gameState.players[0].hand.length,
    );

    // Create a temporary card back at discard position
    const dummyCard: Card = { suit: 'spades', rank: 1, deckIndex: 0, id: 'temp_anim' };
    const tempSprite = new CardSprite(this, fromPos.x, fromPos.y, dummyCard, false);
    tempSprite.setDepth(300);

    await tempSprite.animateTo(targetPos.x, targetPos.y, 400, 0);

    tempSprite.destroy();
    this.animating = false;
  }

  private onMeldClick(meldId: string): void {
    if (!isHumanTurn(this.gameState) || this.animating) return;
    if (!this.gameState.hasDrawnThisTurn) return;

    // If one card is selected, try to add it to this meld
    if (this.selectedCards.length === 1) {
      const result = executeAction(this.gameState, {
        type: 'ADD_TO_MELD',
        cardId: this.selectedCards[0],
        meldId,
      });

      if (result.success) {
        this.gameState = result.state;
        this.clearSelection();
        this.processEvents(result.events);
        this.refreshDisplay();
      } else {
        this.hud.showMessage('Cannot add this card to that meld', 1500);
      }
      return;
    }

    // Set target meld for next card click
    this.targetMeldId = meldId;
    this.hud.showMessage('Select a card to add', 1500);
  }

  private addCardToTargetMeld(card: Card): void {
    if (!this.targetMeldId) return;

    const result = executeAction(this.gameState, {
      type: 'ADD_TO_MELD',
      cardId: card.id,
      meldId: this.targetMeldId,
    });

    this.targetMeldId = null;

    if (result.success) {
      this.gameState = result.state;
      this.processEvents(result.events);
      this.refreshDisplay();
    } else {
      this.hud.showMessage('Cannot add that card', 1500);
    }
  }

  private playSelectedMeld(): void {
    if (this.selectedCards.length < 3) return;

    const result = executeAction(this.gameState, {
      type: 'PLAY_MELD',
      cardIds: this.selectedCards,
    });

    if (result.success) {
      this.gameState = result.state;
      this.clearSelection();
      this.processEvents(result.events);
      this.refreshDisplay();
    } else {
      this.hud.showMessage(result.message || 'Invalid meld', 1500);
    }
  }

  private discardSelectedCard(): void {
    if (this.selectedCards.length !== 1) return;

    const cardId = this.selectedCards[0];
    const result = executeAction(this.gameState, { type: 'DISCARD', cardId });

    if (result.success) {
      this.gameState = result.state;
      this.clearSelection();
      this.processEvents(result.events);
      this.refreshDisplay();
    } else {
      this.hud.showMessage(result.message || 'Cannot discard this card', 1500);
    }
  }

  private onDragDiscard(card: Card): void {
    if (!isHumanTurn(this.gameState) || this.animating) return;
    if (!this.gameState.hasDrawnThisTurn) {
      this.hud.showMessage('Draw a card first!', 1500);
      return;
    }

    const result = executeAction(this.gameState, { type: 'DISCARD', cardId: card.id });
    if (result.success) {
      this.gameState = result.state;
      this.clearSelection();
      this.processEvents(result.events);
      this.refreshDisplay();
    } else {
      this.hud.showMessage(result.message || 'Cannot discard this card', 1500);
      this.refreshDisplay();
    }
  }

  private onDragToMeld(card: Card, meldId: string): void {
    if (!isHumanTurn(this.gameState) || this.animating) return;
    if (!this.gameState.hasDrawnThisTurn) {
      this.hud.showMessage('Draw a card first!', 1500);
      return;
    }

    const result = executeAction(this.gameState, {
      type: 'ADD_TO_MELD',
      cardId: card.id,
      meldId,
    });

    if (result.success) {
      this.gameState = result.state;
      this.clearSelection();
      this.processEvents(result.events);
      this.refreshDisplay();
    } else {
      this.hud.showMessage('Cannot add this card to that meld', 1500);
      this.refreshDisplay();
    }
  }

  private clearSelection(): void {
    this.selectedCards = [];
    this.targetMeldId = null;
    this.playerHand.clearDrawHighlights();
    this.updateSelectionUI();
  }

  // --- AI Turn ---

  private startAITurn(): void {
    if (this.aiThinking) return;
    this.aiThinking = true;

    // Show thinking indicator
    if (this.aiThinkingText) {
      this.aiThinkingText.setVisible(true);
      this.aiThinkingDots = 0;
      this.aiThinkingTimer = this.time.addEvent({
        delay: 400,
        callback: () => {
          this.aiThinkingDots = (this.aiThinkingDots + 1) % 4;
          const dots = '.'.repeat(this.aiThinkingDots);
          this.aiThinkingText?.setText(`AI is thinking${dots}`);
        },
        loop: true,
      });
    }

    // Delay before AI acts
    this.time.delayedCall(1200, () => this.executeAITurn());
  }

  private async executeAITurn(): Promise<void> {
    if (this.gameState.turnPhase === 'round_end' || this.gameState.turnPhase === 'game_over') {
      this.stopAIThinking();
      return;
    }

    this.animating = true;

    const action = getAIAction(this.gameState);

    // Capture state before action for animation context
    const discardTopBefore = this.gameState.discardPile.length > 0
      ? this.gameState.discardPile[this.gameState.discardPile.length - 1]
      : null;

    const result = executeAction(this.gameState, action);

    if (result.success) {
      this.gameState = result.state;

      // Animate based on action type
      if (action.type === 'DRAW_DECK') {
        await this.animateAIDraw('deck');
      } else if (action.type === 'DRAW_DISCARD') {
        await this.animateAIDraw('discard', discardTopBefore ?? undefined);
      } else if (action.type === 'DISCARD') {
        // Find the discarded card (top of discard pile after action)
        const discardedCard = this.gameState.discardPile[this.gameState.discardPile.length - 1];
        await this.animateAIDiscard(discardedCard);
      }

      this.processEvents(result.events);
    }

    this.animating = false;

    // Check if AI turn continues (still AI's turn)
    if (!isHumanTurn(this.gameState) && this.gameState.turnPhase !== 'round_end' && this.gameState.turnPhase !== 'game_over') {
      // Refresh display between AI sub-actions so melds update
      this.refreshDisplayWithoutAITrigger();
      await this.delay(600);
      await this.executeAITurn();
    } else {
      this.stopAIThinking();
      this.refreshDisplay();
    }
  }

  private async animateAIDraw(source: 'deck' | 'discard', discardCard?: Card): Promise<void> {
    const aiHandPos = this.aiHand.getCenterPosition();
    const dummyCard: Card = discardCard ?? { suit: 'spades', rank: 1, deckIndex: 0, id: 'temp_ai_draw' };

    if (source === 'deck') {
      const deckPos = this.deckDisplay.getDeckPosition();
      const tempSprite = new CardSprite(this, deckPos.x, deckPos.y, dummyCard, false);
      tempSprite.setDepth(300);
      await tempSprite.animateTo(aiHandPos.x, aiHandPos.y, 400);
      tempSprite.destroy();
    } else {
      // Draw from discard: show face-up card, animate to AI hand (stays face-up during travel, no flip needed since it goes into hidden hand)
      const discardPos = this.discardPile.getPosition();
      const tempSprite = new CardSprite(this, discardPos.x, discardPos.y, dummyCard, true);
      tempSprite.setDepth(300);
      await tempSprite.animateTo(aiHandPos.x, aiHandPos.y, 400);
      tempSprite.destroy();
    }
  }

  private async animateAIDiscard(discardedCard: Card): Promise<void> {
    const aiHandPos = this.aiHand.getCenterPosition();
    const discardPos = this.discardPile.getPosition();

    // Start face-down at AI hand, flip to face-up as it travels to discard pile
    const tempSprite = new CardSprite(this, aiHandPos.x, aiHandPos.y, discardedCard, false);
    tempSprite.setDepth(300);
    await tempSprite.animateToWithFlip(discardPos.x, discardPos.y, discardedCard, 500);
    tempSprite.destroy();
  }

  /** Refresh display without triggering AI turn (for mid-AI-turn updates) */
  private refreshDisplayWithoutAITrigger(): void {
    this.playerHand.updateCards(this.gameState.players[0].hand, false);
    this.aiHand.updateCards(this.gameState.players[1].hand, false);
    this.playerMelds.updateMelds(this.gameState.players[0].melds);
    this.aiMelds.updateMelds(this.gameState.players[1].melds);
    this.deckDisplay.updateDeckCount(this.gameState.drawPile.length);
    this.deckDisplay.updatePozzetti(this.gameState.pozzetti);
    this.discardPile.updateCards(this.gameState.discardPile);
    this.hud.update(this.gameState);
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => this.time.delayedCall(ms, resolve));
  }

  private stopAIThinking(): void {
    this.aiThinking = false;
    if (this.aiThinkingTimer) {
      this.aiThinkingTimer.destroy();
      this.aiThinkingTimer = undefined;
    }
    if (this.aiThinkingText) {
      this.aiThinkingText.setVisible(false);
    }
  }

  // --- Events ---

  private processEvents(events: GameEvent[]): void {
    for (const event of events) {
      switch (event.type) {
        case 'POZZETTO_TAKEN':
          this.hud.showMessage(
            `${event.playerId === 'human' ? 'You' : 'AI'} took the pozzetto${event.inFlight ? ' in flight!' : '!'}`,
            2000,
          );
          break;
        case 'GOING_OUT':
          this.hud.showMessage(
            `${event.playerId === 'human' ? 'You' : 'AI'} went out!`,
            3000,
          );
          break;
        case 'ROUND_END':
          this.handleRoundEnd(event.reason);
          break;
        case 'GAME_OVER':
          this.handleGameOver(event.winnerId);
          break;
      }
    }
  }

  private handleRoundEnd(reason: string): void {
    this.stopAIThinking();

    const reasons: Record<string, string> = {
      going_out: 'Player went out!',
      deck_empty: 'Draw pile exhausted!',
      stalemate: 'Stalemate!',
    };

    // Show round summary after a delay
    this.time.delayedCall(2000, () => {
      this.showRoundSummary(reasons[reason] || 'Round over');
    });
  }

  private showRoundSummary(reason: string): void {
    const overlay = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.8)
      .setDepth(500).setInteractive();

    const container = this.add.container(GAME_WIDTH / 2, GAME_HEIGHT / 2).setDepth(501);

    const bg = this.add.rectangle(0, 0, 450, 350, COLORS.UI_BG, 0.95)
      .setStrokeStyle(2, COLORS.UI_BORDER);
    container.add(bg);

    const title = this.add.text(0, -150, reason, {
      fontSize: '28px',
      color: '#f1c40f',
      fontFamily: 'Georgia, serif',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    container.add(title);

    // Scores
    const p0 = this.gameState.players[0];
    const p1 = this.gameState.players[1];

    const p0Score = calculateRoundScore(
      p0.melds, p0.hand,
      this.gameState.roundScores.length > 0,
      p0.tookPozzetto,
    );
    const p1Score = calculateRoundScore(
      p1.melds, p1.hand, false, p1.tookPozzetto,
    );

    const scoreLines = [
      `Your Score: ${p0.totalScore}`,
      `  Melds: +${p0Score.meldPoints}  Hand: -${p0Score.handPenalty}`,
      `  Burracos: +${p0Score.burracoBonus}  Going out: +${p0Score.goingOutBonus}`,
      '',
      `AI Score: ${p1.totalScore}`,
      `  Melds: +${p1Score.meldPoints}  Hand: -${p1Score.handPenalty}`,
      `  Burracos: +${p1Score.burracoBonus}`,
    ];

    const scoreText = this.add.text(0, -20, scoreLines.join('\n'), {
      fontSize: '16px',
      color: '#cccccc',
      fontFamily: 'Georgia, serif',
      align: 'center',
      lineSpacing: 6,
    }).setOrigin(0.5);
    container.add(scoreText);

    // Continue button
    const btnBg = this.add.rectangle(0, 130, 200, 45, COLORS.BUTTON, 0.9)
      .setInteractive({ useHandCursor: true })
      .on('pointerover', () => btnBg.setFillStyle(COLORS.BUTTON_HOVER, 1))
      .on('pointerout', () => btnBg.setFillStyle(COLORS.BUTTON, 0.9))
      .on('pointerdown', () => {
        overlay.destroy();
        container.destroy();

        if (this.gameState.turnPhase === 'game_over') {
          this.scene.start('MenuScene');
        } else {
          const result = executeAction(this.gameState, { type: 'NEW_ROUND' });
          this.gameState = result.state;
          this.refreshDisplay();
        }
      });
    container.add(btnBg);

    const btnLabel = this.gameState.turnPhase === 'game_over' ? 'Back to Menu' : 'Next Round';
    const btnText = this.add.text(0, 130, btnLabel, {
      fontSize: '18px',
      color: COLORS.TEXT_LIGHT,
      fontFamily: 'Georgia, serif',
    }).setOrigin(0.5);
    container.add(btnText);
  }

  private handleGameOver(winnerId: string): void {
    this.stopAIThinking();
    const msg = winnerId === 'human' ? 'You Win!' : 'AI Wins!';
    this.hud.showMessage(msg, 5000);
  }

  private showQuitConfirmation(): void {
    const overlay = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.7)
      .setDepth(500).setInteractive();

    const container = this.add.container(GAME_WIDTH / 2, GAME_HEIGHT / 2).setDepth(501);

    const bg = this.add.rectangle(0, 0, 360, 180, COLORS.UI_BG, 0.95)
      .setStrokeStyle(2, COLORS.UI_BORDER);
    container.add(bg);

    const title = this.add.text(0, -50, 'Quit Game?', {
      fontSize: '24px',
      color: '#f1c40f',
      fontFamily: 'Georgia, serif',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    container.add(title);

    const subtitle = this.add.text(0, -15, 'Your progress will be lost.', {
      fontSize: '15px',
      color: '#aaaaaa',
      fontFamily: 'Georgia, serif',
    }).setOrigin(0.5);
    container.add(subtitle);

    // Quit button
    const quitBg = this.add.rectangle(-80, 45, 120, 40, 0xc0392b, 0.9)
      .setInteractive({ useHandCursor: true })
      .on('pointerover', () => quitBg.setFillStyle(0xe74c3c, 1))
      .on('pointerout', () => quitBg.setFillStyle(0xc0392b, 0.9))
      .on('pointerdown', () => {
        this.stopAIThinking();
        overlay.destroy();
        container.destroy();
        this.scene.start('MenuScene');
      });
    container.add(quitBg);

    const quitText = this.add.text(-80, 45, 'Quit', {
      fontSize: '16px',
      color: COLORS.TEXT_LIGHT,
      fontFamily: 'Georgia, serif',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    container.add(quitText);

    // Cancel button
    const cancelBg = this.add.rectangle(80, 45, 120, 40, COLORS.BUTTON, 0.9)
      .setInteractive({ useHandCursor: true })
      .on('pointerover', () => cancelBg.setFillStyle(COLORS.BUTTON_HOVER, 1))
      .on('pointerout', () => cancelBg.setFillStyle(COLORS.BUTTON, 0.9))
      .on('pointerdown', () => {
        overlay.destroy();
        container.destroy();
      });
    container.add(cancelBg);

    const cancelText = this.add.text(80, 45, 'Continue', {
      fontSize: '16px',
      color: COLORS.TEXT_LIGHT,
      fontFamily: 'Georgia, serif',
    }).setOrigin(0.5);
    container.add(cancelText);
  }

  update(): void {
    // Continuous updates if needed
  }
}
